'use client';

import type { SmokeTestHistoryResponseDto } from '@terra/shared-contracts-smoke-test-history';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  ChatHarProvider,
  extractLatestCoPilotPlan,
  prepareTraceHistoryMessages,
  TraceFilesProvider,
  TraceView,
  useTraceViewSettings,
} from '@terra/ui-design-system';
import { useCallback, useMemo } from 'react';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useHarById } from '../../hooks/api/useHarById';
import { useHarDisplayConfig } from '../../hooks/useHarDisplayConfig';
import { useToolHarPresence } from '../../hooks/useToolHarPresence';
import {
  CoPilotTraceExportButton,
  useCoPilotExportChatTitle,
  useCoPilotSessionExportTargetId,
} from './session-export';
import { CoPilotTraceSummaryView } from './CoPilotTraceSummaryView';
import { resolveCoPilotTraceHistoryStatus } from '../../utils/resolve-co-pilot-trace-status';

type CoPilotTraceViewProps = {
  threadId: string;
  applicationId?: string;
  messages: TerraUIMessage[];
  status?: 'running' | 'completed' | 'error' | 'aborted';
  isLoading?: boolean;
};

export function CoPilotTraceView({
  threadId,
  applicationId,
  messages,
  status = 'completed',
  isLoading = false,
}: CoPilotTraceViewProps) {
  const { fetchHarById } = useHarById();
  const { hasHarPresence } = useToolHarPresence(threadId);
  const harDisplay = useHarDisplayConfig(applicationId);
  const chatTitle = useCoPilotExportChatTitle(threadId);
  const exportTargetId = useCoPilotSessionExportTargetId({
    threadId,
    targetId: applicationId,
  });
  const { settings } = useTraceViewSettings();
  const showDetailedView = settings.showDetailedView;

  const fetchHarData = useCallback(
    async ({
      threadId,
      toolCallId,
    }: {
      threadId: string;
      toolCallId: string;
    }) => {
      try {
        const response = await terraProxyApiClient.getToolHarsByToolCallId({
          threadId,
          toolCallId,
        });

        return response?.hars ?? [];
      } catch (error) {
        console.error('[CoPilotTraceView] Error fetching HAR data:', error);

        return [];
      }
    },
    [],
  );

  const traceMessages = useMemo(
    () =>
      prepareTraceHistoryMessages(messages, {
        isLoading: status === 'running',
      }),
    [messages, status],
  );

  const plan = useMemo(
    () => extractLatestCoPilotPlan(messages, 'co-pilot'),
    [messages],
  );

  const history = useMemo((): SmokeTestHistoryResponseDto => {
    return {
      threadId,
      messages: traceMessages,
      status: resolveCoPilotTraceHistoryStatus(status),
      plan,
    };
  }, [threadId, traceMessages, status, plan]);

  const exportButton = useMemo(
    () => (
      <CoPilotTraceExportButton
        threadId={threadId}
        messages={messages}
        applicationId={exportTargetId}
        chatTitle={chatTitle}
        status={status}
        disabled={isLoading}
      />
    ),
    [exportTargetId, chatTitle, isLoading, messages, status, threadId],
  );

  return (
    <ChatHarProvider
      threadId={threadId}
      fetchHarData={fetchHarData}
      fetchHarById={fetchHarById}
      hasHarPresence={hasHarPresence}
      harDisplay={harDisplay}
    >
      <TraceFilesProvider traceFiles={[]}>
        {showDetailedView ? (
          <TraceView
            history={history}
            maxHeight="100%"
            status={status}
            isLoading={isLoading}
            tracePhase="co-pilot"
            showToolbar
            toolbarExtra={exportButton}
          />
        ) : (
          <CoPilotTraceSummaryView
            threadId={threadId}
            targetId={exportTargetId}
            plan={plan}
            status={status}
            enabled={Boolean(threadId)}
          />
        )}
      </TraceFilesProvider>
    </ChatHarProvider>
  );
}
